import type { HTMLAttributes, ReactNode } from 'react';
import { Checkmark } from '../../../icons';
import { cx } from '../../../utils/cx';

export interface CardProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Title displayed inside the card */
  title: ReactNode;
  /** Optional leading icon or element */
  icon?: ReactNode;
  /** Optional action element shown in the footer (e.g., a button) */
  action?: ReactNode;
  /** Shows a selectable indicator when true */
  selectable?: boolean;
  /** Controls the selected state of the indicator */
  selected?: boolean;
  /** Callback fired when the selection indicator is toggled */
  onSelectChange?: (selected: boolean) => void;
  /** Accessible label for the selection indicator button */
  selectionLabel?: string;
  /** When true, dims the card and disables the selection button */
  disabled?: boolean;
}

export function Card({
  title,
  icon,
  action,
  selectable,
  selected,
  onSelectChange,
  selectionLabel = 'Select card',
  disabled,
  className,
  ...props
}: CardProps) {
  const handleToggle = () => {
    if (!selectable || disabled) {
      return;
    }

    onSelectChange?.(!selected);
  };

  return (
    <div
      {...props}
      className={cx(
        'w-full rounded-[32px] border border-neutral-200 bg-neutral-50 p-6 text-left shadow-sm transition-shadow',
        selected && 'border-[var(--gn-color-brand-primary-300)] shadow-md',
        disabled && 'pointer-events-none opacity-60',
        className
      )}
    >
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-4">
          {icon && (
            <div className="flex size-14 items-center justify-center rounded-2xl bg-white text-[var(--gn-color-brand-primary-500)] shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
              {icon}
            </div>
          )}
          <div className="flex-1">
            <p className="text-base font-semibold text-gray-900 md:text-lg">
              {title}
            </p>
          </div>
          {selectable && (
            <button
              type="button"
              aria-pressed={selected}
              aria-label={selectionLabel}
              disabled={disabled}
              onClick={handleToggle}
              className={cx(
                'flex size-11 items-center justify-center rounded-full border-2 transition',
                selected
                  ? 'border-[var(--gn-color-brand-primary-500)] bg-white text-[var(--gn-color-brand-primary-500)] shadow-[0_1px_2px_rgba(15,23,42,0.12)]'
                  : 'border-[var(--gn-color-brand-primary-200)] bg-white text-transparent hover:border-[var(--gn-color-brand-primary-300)]'
              )}
            >
              <Checkmark className="size-5" />
            </button>
          )}
        </div>
        {action && <div className="pt-2">{action}</div>}
      </div>
    </div>
  );
}
